/**
 * This is the Billing-Organizations middleware.
 * APIs are called for CRUD operations on the list.
 * @param store
 * @returns {function(*): function(...[*]=)}
 */

import API from "../../common/utils/API";
import MARVEL from "../../common/utils/MarvelUrls";
import { setRouterLink } from "../../router/RouterActions";

import {
    fetchBillingOrganizationsTableData,
    toggleAddBillingOrgModal,
    toggleDeleteBillingOrgModal,
    toggleEditBillingOrgModal,
    billingOrganizationsTableDataSuccess,
    getOrganizationDetailSuccess,
    updatedOrgRefresh,
    toggleCPModal,
    orgTenantsAndCountriesSuccess,
    orgTenantsAndCountriesError
} from "../../billing/organizations/actions/BillingOrganizationsActions";
import {apiActionsBillingOrg} from "../../billing/organizations/constants/BillingOrganizationsActionConstants";
import {activityLoggerActionTypes} from "../veritas-middleware/constants/UtilityMiddlewareConstants";
import {toUpperCaseSentence} from "../../common/utils/ToUpperCaseSentence";
import {displayDuplicateMessage, displayErrorMessage} from "../../common/marvel-reducers/formMessageActions";
import {
    generatePDFReportForOneTenant,
    generateXLSReportForOneTenant
} from "../../report-generation/veritas-report-gen/ReportGeneratorActions";
import {ReportsType} from "../../report-generation/veritas-report-gen/ReportGeneratorActionConstants";

const billingOrganizationsMiddleware = (store) => (next) => (action) => {

    switch (action.type) {

        case apiActionsBillingOrg.GET: {
            next(action);

            const {authToken, orgID} = action.payload;
            API.getOneBillingOrg(authToken.idToken, orgID)
                .then(res => {
                    store.dispatch(getOrganizationDetailSuccess(res.data))
                }).catch(err => {
                console.error(err);
                throw new err();
            });

            break;
        }

        case apiActionsBillingOrg.GET_ALL: {
            next(action);

            const { authToken } = action.payload;
            API.getAllBillingOrganizations(authToken.idToken)
                .then(res => {
                    store.dispatch(billingOrganizationsTableDataSuccess(res.data))
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case apiActionsBillingOrg.GET_SERVICE_IDS: {
            next(action);

            const { authToken, useCase, tenantName, type } = action.payload;
            API.getAssociatedServiceIds(authToken.idToken, tenantName)
                .then(res => {
                    if (type === ReportsType.PDF) {
                        store.dispatch(generatePDFReportForOneTenant(res.data));
                    } else if (type === ReportsType.XLSX) {
                        store.dispatch(generateXLSReportForOneTenant(useCase, res.data));
                    } else {
                        console.error("Wrong Action Dispatched.")
                    }
                }).catch(err => console.log(err));

            break;
        }

        case apiActionsBillingOrg.POST: {
            next(action);

            const { authToken, billingOrgObject } = action.payload;
            API.postBillingOrg(authToken.idToken, billingOrgObject)
                .then(res => {
                    const log = {
                        object_id: res.data.id,
                        object_type: "billing_org_create",
                        user: authToken.account.name
                    };
                    store.dispatch({
                        type: activityLoggerActionTypes.LOG_CHANGE,
                        payload: { log, authToken }
                    });
                    store.dispatch(toggleAddBillingOrgModal());
                    store.dispatch(fetchBillingOrganizationsTableData());
                    store.dispatch(setRouterLink(MARVEL.BILLING.organizations + res.data.id))
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case apiActionsBillingOrg.PUT: {
            next(action);

            const { authToken, billingOrgObject, orgID } = action.payload;

            API.putBillingOrg(authToken.idToken, orgID, billingOrgObject)
                .then(res => {
                    const log = {
                        object_id: res.data.id,
                        object_type: "billing_org_edit",
                        user: authToken.account.name
                    };
                    store.dispatch({
                        type: activityLoggerActionTypes.LOG_CHANGE,
                        payload: { log, authToken }
                    });
                    store.dispatch(toggleEditBillingOrgModal());
                    store.dispatch(updatedOrgRefresh());
                    store.dispatch(fetchBillingOrganizationsTableData());
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case apiActionsBillingOrg.DELETE_CURRENT_ORG: {
            next(action);

            const { authToken, orgID } = action.payload;
            API.deleteBillingOrg(authToken.idToken, orgID)
                .then(res => {
                    const log = {
                        object_id: res.data.id,
                        object_type: "billing_org_delete",
                        user: authToken.account.name
                    };
                    store.dispatch({
                        type: activityLoggerActionTypes.LOG_CHANGE,
                        payload: { log, authToken }
                    });
                    store.dispatch(toggleDeleteBillingOrgModal());
                    store.dispatch(setRouterLink(MARVEL.BILLING.organizations));

                }).catch(error => {
                const errorObject = error.response.data
                const message = [];
                // pretty print the error message for the user
                // eslint-disable-next-line no-unused-vars
                for (let [key, value] of Object.entries(errorObject)) {
                    message.push(toUpperCaseSentence(value[0]));
                }
                store.dispatch(displayErrorMessage(message, true));
            });

            break;
        }

        case apiActionsBillingOrg.CREATE_CP_USER:
            next(action);

            const {authToken, portalPayload} = action.payload;

            API.createNewUserForCP(authToken.idToken, portalPayload)
                .then(res => {
                    API.postResetPasswordRequest(res.data.email)
                        .then(resetSuccess => {
                            store.dispatch(toggleCPModal())
                            store.dispatch(updatedOrgRefresh())
                        })
                        .catch(err => {
                            console.error(err)
                        })
                })
                .catch(error => {
                    console.error(error)
                    const errorObject = error.response.data;
                    const message = [];
                    // pretty print the error message for the user
                    // eslint-disable-next-line no-unused-vars
                    for (let [key, value] of Object.entries(errorObject)) {
                        message.push(toUpperCaseSentence(value));
                    }
                    if (errorObject['duplicate_message']) {
                        store.dispatch(displayDuplicateMessage(message, true));
                    } else {
                        store.dispatch(displayErrorMessage(message, true));
                    }
                })

            break;

        case apiActionsBillingOrg.GET_ORG_TENANTS_AND_COUNTRIES: {
            next(action);

            const { authToken } = action.payload;
            API.getOrgTenantsAndCountriesWithId(authToken.idToken)
                .then(res => {
                    const data = Array.isArray(res.data) ? res.data : [];
                    store.dispatch(orgTenantsAndCountriesSuccess(data));
                }).catch(err => {
                console.error(err);
                store.dispatch(orgTenantsAndCountriesError());
            });

            break;
        }

        default: next(action);
    }
}

export default billingOrganizationsMiddleware;
